import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Activity,
  BarChart2,
  BookOpen,
  Bot,
  CreditCard,
  FileSpreadsheet,
  FileText,
  HardDrive,
  Globe,
  Headphones,
  History,
  Home,
  Link as LinkIcon,
  LogOut,
  LayoutGrid,
  MessageSquare,
  Mic,
  PenTool,
  Presentation,
  Receipt,
  Scale,
  ScanLine,
  Search,
  Settings,
  Shield,
  Sparkles,
  User,
  Users,
  Video
} from 'lucide-react';
import { aiAppVotesAPI, authAPI, notificationAPI, companyAPI, serviceMenuAPI } from '../utils/api';
import NotificationsModal from './NotificationsModal';
import {
  getBrowserNotificationStatus,
  htmlToText,
  requestBrowserNotificationPermission,
  showBrowserNotification
} from '../utils/browserNotifications';

const PRIMARY_MENU_ITEMS = [
  { name: 'Личный кабинет', route: '/dashboard', icon: Home },
  { name: 'Конструктор', route: '/app-builder', icon: LayoutGrid },
  { name: 'История', route: '/ai-history', icon: History }
];

const SECONDARY_MENU_ITEMS = [
  { key: 'knowledge_base', name: 'База знаний', route: '/knowledge-base', icon: BookOpen },
  { key: 'support', name: 'Помощь', route: '/support', icon: Headphones },
  { key: 'pricing', name: 'Тарифы', route: '/pricing', icon: BarChart2 },
  { key: 'billing', name: 'Биллинг', route: '/billing', icon: CreditCard },
  { key: 'profile', name: 'Профиль', route: '/profile', icon: User },
  { key: 'api_docs', name: 'Документация', route: '/api-docs', icon: BookOpen }
];

const APP_ROUTE_ICONS = {
  '/ai-chat': MessageSquare,
  '/transcription': Mic,
  '/web-analyst': BarChart2,
  '/legal-assistant': Scale,
  '/legal-assistant-v2': Scale,
  '/hr-agent': Users,
  '/rag-v2': Search,
  '/content-factory': PenTool,
  '/deeplog': Activity,
  '/ocr-scanner': ScanLine,
  '/tech-writer': FileText,
  '/bi-system': BarChart2,
  '/ai-presentation': Presentation,
  '/ai-polygon': Sparkles,
  '/vidzhey': Video,
  '/smart-links': LinkIcon,
  '/site-analyzer': Globe,
  '/osv-analysis': FileSpreadsheet,
  '/zao-module': Receipt,
  '/disk': HardDrive
};

const isRouteActive = (pathname, route) => pathname === route || pathname.startsWith(`${route}/`);

const getAppIcon = (name = '', route = '') => {
  if (APP_ROUTE_ICONS[route]) {
    return APP_ROUTE_ICONS[route];
  }

  const normalized = `${name} ${route}`.toLowerCase();

  if (normalized.includes('чат')) return MessageSquare;
  if (normalized.includes('транск') || normalized.includes('audio') || normalized.includes('video')) return Mic;
  if (normalized.includes('юрист') || normalized.includes('legal')) return Scale;
  if (normalized.includes('hr')) return Users;
  if (normalized.includes('поиск') || normalized.includes('rag') || normalized.includes('search')) return Search;
  if (normalized.includes('контент') || normalized.includes('factory')) return PenTool;
  if (normalized.includes('deeplog') || normalized.includes('лог')) return Activity;
  if (normalized.includes('скан') || normalized.includes('ocr')) return ScanLine;
  if (normalized.includes('писател') || normalized.includes('writer')) return FileText;
  if (normalized.includes('презент') || normalized.includes('presentation')) return Presentation;
  if (normalized.includes('аналит') || normalized.includes('bi')) return BarChart2;
  if (normalized.includes('ссыл') || normalized.includes('link')) return LinkIcon;
  if (normalized.includes('диск') || normalized.includes('/disk')) return HardDrive;
  if (normalized.includes('осв') || normalized.includes('сальд')) return FileSpreadsheet;
  if (normalized.includes('зао') || normalized.includes('аванс')) return Receipt;

  return Bot;
};

const Sidebar = ({ mobileOnly = false, open, onOpenChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isControlled = typeof open === 'boolean';
  const [internalSidebarOpen, setInternalSidebarOpen] = useState(false);
  const isSidebarOpen = isControlled ? open : internalSidebarOpen;
  const setIsSidebarOpen = useCallback((nextValue) => {
    if (isControlled) {
      onOpenChange?.(nextValue);
      return;
    }
    setInternalSidebarOpen(nextValue);
  }, [isControlled, onOpenChange]);
  const [user, setUser] = useState(null);
  const [aiAppsMenuItems, setAiAppsMenuItems] = useState([]);
  const [serviceMenuItems, setServiceMenuItems] = useState(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationStats, setNotificationStats] = useState({ unread_notifications: 0 });
  const latestBrowserNotificationIdRef = useRef(null);
  const notificationStatsLoadingRef = useRef(false);
  const latestNotificationCheckLoadingRef = useRef(false);
  const latestUnreadNotificationIdRef = useRef(null);
  const hasLatestUnreadBaselineRef = useRef(false);
  const [activeCompany, setActiveCompany] = useState(null);

  const loadApps = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      const isInCompanyMode = localStorage.getItem('isInCompanyMode') === 'true';

      if (isInCompanyMode) {
        try {
          const allowedResponse = await companyAPI.getAllowedApps();
          if (allowedResponse.apps && allowedResponse.apps.length > 0) {
            setAiAppsMenuItems(
              allowedResponse.apps.map((app) => ({
                id: app.id,
                name: app.name,
                route: app.route
              }))
            );
            return;
          }

          if (allowedResponse.has_permissions_configured) {
            setAiAppsMenuItems([]);
            return;
          }
        } catch (err) {
          console.warn('Не удалось загрузить разрешённые приложения, загружаем все:', err);
        }
      }

      const response = await aiAppVotesAPI.getMenuApps();
      if (response.apps && response.apps.length > 0) {
        setAiAppsMenuItems(
          response.apps.map((app) => ({
            id: app.id,
            name: app.name,
            route: app.route
          }))
        );
      }
    } catch (error) {
      console.error('Ошибка загрузки списка приложений:', error);
      setAiAppsMenuItems([
        { id: 'ai_chat', name: 'AI Чат', route: '/ai-chat' },
        { id: 'transcriber', name: 'Транскрибация', route: '/transcription' },
        { id: 'web_analyst', name: 'AI-Аналитик', route: '/web-analyst' },
        { id: 'legal_assistant_v2', name: 'AI Юрист', route: '/legal-assistant-v2' },
        { id: 'hr_agent', name: 'HR Агент', route: '/hr-agent' },
        { id: 'rag_v2', name: 'Умный поиск', route: '/rag-v2' },
        { id: 'content_factory', name: 'Контент-Завод', route: '/content-factory' },
        { id: 'deeplog', name: 'DeepLog', route: '/deeplog' },
        { id: 'ocr_scanner', name: 'AI Сканер', route: '/ocr-scanner' },
        { id: 'tech_writer', name: 'Технический писатель', route: '/tech-writer' },
        { id: 'bi_system', name: 'БИ Система', route: '/bi-system' }
      ]);
    }
  };

  const handleAppMenuClick = () => {
    closeSidebar();
  };

  const loadServiceMenuSettings = async () => {
    try {
      const response = await serviceMenuAPI.getSettings();
      setServiceMenuItems(response.items || []);
    } catch (error) {
      console.error('Ошибка загрузки настроек сервисного меню:', error);
      setServiceMenuItems(null);
    }
  };

  useEffect(() => {
    loadApps();
    loadServiceMenuSettings();
  }, []);

  useEffect(() => {
    const handleAuthChange = () => {
      loadApps();
    };

    window.addEventListener('authStatusChanged', handleAuthChange);
    return () => window.removeEventListener('authStatusChanged', handleAuthChange);
  }, []);

  useEffect(() => {
    const userRole = localStorage.getItem('userRole');
    const userEmail = localStorage.getItem('userEmail');
    const hasAdminAccess = localStorage.getItem('hasAdminAccess') === 'true' || userRole === 'admin';
    const companyData = localStorage.getItem('activeCompany');
    const isInCompanyMode = localStorage.getItem('isInCompanyMode') === 'true';

    if (userRole) {
      setUser({ role: userRole, email: userEmail, hasAdminAccess });
    }

    if (companyData && isInCompanyMode) {
      try {
        setActiveCompany(JSON.parse(companyData));
      } catch (e) {
        setActiveCompany(null);
      }
    } else {
      setActiveCompany(null);
    }

    const handleAuthChange = () => {
      const nextUserRole = localStorage.getItem('userRole');
      const nextUserEmail = localStorage.getItem('userEmail');
      setUser(nextUserRole ? {
        role: nextUserRole,
        email: nextUserEmail,
        hasAdminAccess: localStorage.getItem('hasAdminAccess') === 'true' || nextUserRole === 'admin',
      } : null);
      const newCompanyData = localStorage.getItem('activeCompany');
      const newIsInCompanyMode = localStorage.getItem('isInCompanyMode') === 'true';

      if (newCompanyData && newIsInCompanyMode) {
        try {
          setActiveCompany(JSON.parse(newCompanyData));
        } catch (e) {
          setActiveCompany(null);
        }
      } else {
        setActiveCompany(null);
      }
    };

    window.addEventListener('authStatusChanged', handleAuthChange);
    return () => window.removeEventListener('authStatusChanged', handleAuthChange);
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('accessToken')) return undefined;

    let isMounted = true;
    const refreshProfile = async () => {
      try {
        const profile = await authAPI.getProfile();
        if (isMounted) {
          setUser({
            role: profile.role,
            email: profile.email,
            hasAdminAccess: Boolean(profile.has_admin_access),
          });
        }
      } catch (error) {
        console.error('Ошибка обновления профиля для навигации:', error);
      }
    };

    refreshProfile();
    return () => {
      isMounted = false;
    };
  }, []);

  const checkLatestUnreadNotification = async () => {
    if (latestNotificationCheckLoadingRef.current) {
      return;
    }
    latestNotificationCheckLoadingRef.current = true;

    try {
      const response = await notificationAPI.getNotifications(0, 1, false);
      const latestNotification = Array.isArray(response?.notifications)
        ? response.notifications[0]
        : null;

      if (!latestNotification?.id) {
        hasLatestUnreadBaselineRef.current = true;
        latestUnreadNotificationIdRef.current = null;
        return;
      }

      const latestId = String(latestNotification.id);

      if (!hasLatestUnreadBaselineRef.current) {
        hasLatestUnreadBaselineRef.current = true;
        latestUnreadNotificationIdRef.current = latestId;
        return;
      }

      if (latestUnreadNotificationIdRef.current === latestId) return;

      latestUnreadNotificationIdRef.current = latestId;
      if (latestBrowserNotificationIdRef.current === latestId) return;
      latestBrowserNotificationIdRef.current = latestId;

      const notificationResult = showBrowserNotification({
        title: latestNotification.title || 'Новое уведомление',
        body: htmlToText(latestNotification.message),
        tag: `notification-${latestId}`,
        onClick: () => {
          if (typeof window !== 'undefined') {
            window.focus();
            setNotificationsOpen(true);
          }
        }
      });

      if (!notificationResult?.shown) {
        toast.info(
          <div>
            <div className="font-semibold">{latestNotification.title || 'Новое уведомление'}</div>
            <div className="mt-1 text-sm">{htmlToText(latestNotification.message)}</div>
          </div>,
          { autoClose: 7000 }
        );
        console.info('Системное уведомление не показано:', notificationResult?.reason);
      }
    } catch (error) {
      console.error('Ошибка показа браузерного уведомления:', error);
    } finally {
      latestNotificationCheckLoadingRef.current = false;
    }
  };

  const isServiceItemVisible = (key) => {
    if (!serviceMenuItems) return true;
    const setting = serviceMenuItems.find((item) => item.key === key);
    return setting ? setting.is_enabled !== false : true;
  };

  const visibleSecondaryMenuItems = SECONDARY_MENU_ITEMS.filter((item) => isServiceItemVisible(item.key));

  const loadNotificationStats = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    if (notificationStatsLoadingRef.current) {
      return;
    }
    notificationStatsLoadingRef.current = true;

    try {
      const stats = await notificationAPI.getStats();
      if (stats?.service_unavailable) {
        return;
      }

      if (stats && typeof stats.unread_notifications === 'number') {
        setNotificationStats(stats);
        await checkLatestUnreadNotification();
      } else {
        setNotificationStats({ unread_notifications: 0 });
      }
    } catch (error) {
      if ([502, 503, 504].includes(error?.status)) {
        return;
      }
      console.error('Ошибка загрузки статистики уведомлений:', error);
      setNotificationStats({ unread_notifications: 0 });
    } finally {
      notificationStatsLoadingRef.current = false;
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    loadNotificationStats();

    const interval = setInterval(loadNotificationStats, 60000);
    const handleNotificationUpdate = () => {
      loadNotificationStats();
    };

    window.addEventListener('notificationUpdate', handleNotificationUpdate);

    return () => {
      clearInterval(interval);
      window.removeEventListener('notificationUpdate', handleNotificationUpdate);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    requestBrowserNotificationPermission()
      .then(() => {
        const status = getBrowserNotificationStatus();
        if (status !== 'granted') {
          console.info('Статус системных уведомлений:', status);
        }
      })
      .catch(() => {});
  }, []);

  const getUserDisplayName = () => {
    const userEmail = localStorage.getItem('userEmail');
    if (userEmail) {
      return userEmail.split('@')[0];
    }
    return 'Пользователь';
  };

  const handleLogout = () => {
    latestBrowserNotificationIdRef.current = null;
    notificationStatsLoadingRef.current = false;
    latestNotificationCheckLoadingRef.current = false;
    latestUnreadNotificationIdRef.current = null;
    hasLatestUnreadBaselineRef.current = false;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('activeCompany');
    localStorage.removeItem('isInCompanyMode');
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('impersonatorEmail');
    localStorage.removeItem('impersonatorId');
    window.dispatchEvent(new Event('authStatusChanged'));
    navigate('/login');
  };

  const closeSidebar = () => setIsSidebarOpen(false);
  const adminSectionActive = location.pathname.startsWith('/admin');

  const getItemClassName = (isActive, isEmphasized = false) =>
    `group flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm font-medium transition-all duration-200 ${
      isActive
        ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md shadow-indigo-100/60'
        : isEmphasized
          ? 'bg-blue-50 text-gray-900 hover:bg-blue-100/80'
          : 'text-gray-700 hover:bg-gray-50/90 hover:text-gray-900'
    }`;

  const getIconWrapClassName = (isActive, isEmphasized = false) =>
    `flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${
      isActive
        ? 'bg-white/15 text-white'
        : isEmphasized
          ? 'bg-white text-blue-600'
          : 'bg-gray-100/80 text-gray-500 group-hover:bg-white'
    }`;

  const getAppItemClassName = (isActive) =>
    `group flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm transition-all duration-200 ${
      isActive
        ? 'bg-blue-50 text-blue-700'
        : 'text-gray-600 hover:bg-gray-50/90 hover:text-gray-900'
    }`;

  const getAppIconWrapClassName = (isActive) =>
    `flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors ${
      isActive
        ? 'bg-white text-blue-600'
        : 'bg-gray-100/80 text-gray-500 group-hover:bg-white'
    }`;

  const roleBadgeClassName = activeCompany?.role === 'owner'
    ? 'bg-amber-50 text-amber-700'
    : activeCompany?.role === 'admin'
      ? 'bg-purple-50 text-purple-700'
      : 'bg-blue-50 text-blue-700';

  return (
    <>
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {!mobileOnly && <div className="hidden w-64 flex-shrink-0 lg:block" aria-hidden="true" />}

      <aside
        className={`fixed left-0 top-0 z-50 flex h-screen w-64 flex-col overflow-hidden border-r border-gray-200/70 bg-white/95 shadow-xl backdrop-blur-sm transition-transform duration-300 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${mobileOnly ? 'lg:hidden' : 'lg:translate-x-0 lg:flex'}`}
      >
        <div className="scrollbar-none min-h-0 flex-1 overflow-y-auto">
          <div className="p-4">
            <Link
              to="/dashboard"
              className="mb-4 flex items-center gap-3 px-1 py-1 hover:opacity-80 transition-opacity"
              onClick={closeSidebar}
            >
              <img src="/aihublogo.png" alt="Логотип" className="h-18 w-48 rounded-lg" />
            </Link>

            <div
              className={`mb-4 rounded-2xl p-3 ${
                activeCompany
                  ? 'bg-gradient-to-br from-indigo-50 via-white to-purple-50'
                  : 'bg-gray-50/90'
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                    activeCompany
                      ? 'bg-gradient-to-br from-blue-500 to-indigo-500 text-white shadow-sm shadow-indigo-100'
                      : 'bg-white text-gray-500'
                  }`}
                >
                  {activeCompany ? <Users className="h-4 w-4" /> : <User className="h-4 w-4" />}
                </div>

                <div className="min-w-0 flex-1">
                  <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${activeCompany ? 'text-indigo-600' : 'text-gray-400'}`}>
                    {activeCompany ? 'Режим компании' : 'Личный режим'}
                  </p>
                  <p className="mt-0.5 truncate text-sm font-semibold text-gray-900" title={activeCompany?.name || 'Персональное пространство'}>
                    {activeCompany?.name || 'Персональное пространство'}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {activeCompany ? 'Рабочая навигация компании активна' : 'Все личные разделы доступны в одном меню'}
                  </p>
                </div>
              </div>

              {activeCompany && isServiceItemVisible('profile') && (
                <div className="mt-3 flex items-center justify-between gap-2">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${roleBadgeClassName}`}>
                    {activeCompany.role === 'owner' ? 'Владелец'
                      : activeCompany.role === 'admin' ? 'Администратор'
                      : 'Участник'}
                  </span>
                  <Link
                    to="/profile"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800"
                    onClick={closeSidebar}
                  >
                    <Settings className="h-4 w-4" />
                    Управление
                  </Link>
                </div>
              )}
            </div>

            <nav className="space-y-3">
              <section className="space-y-1.5">
                <div className="px-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                  Основное
                </div>
                <div className="space-y-0.5">
                  {PRIMARY_MENU_ITEMS.map((item) => {
                    const Icon = item.icon;
                    const isActive = isRouteActive(location.pathname, item.route);

                    return (
                      <Link
                        key={item.route}
                        to={item.route}
                        className={getItemClassName(isActive)}
                        onClick={closeSidebar}
                      >
                        <span className={getIconWrapClassName(isActive)}>
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1 truncate">{item.name}</span>
                      </Link>
                    );
                  })}
                </div>
              </section>

              <section className="space-y-1.5">
                <div className="flex items-center justify-between gap-2 px-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                  <span>Приложения</span>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs tracking-normal text-gray-500">
                    {aiAppsMenuItems.length}
                  </span>
                </div>
                <div className="space-y-0.5">
                  {aiAppsMenuItems.length > 0 ? (
                    aiAppsMenuItems.map((item) => {
                      const Icon = getAppIcon(item.name, item.route);
                      const isActive = isRouteActive(location.pathname, item.route);

                      return (
                        <Link
                          key={item.route}
                          to={item.route}
                          className={getAppItemClassName(isActive)}
                          onClick={handleAppMenuClick}
                          title={item.name}
                        >
                          <span className={getAppIconWrapClassName(isActive)}>
                            <Icon className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 flex-1 truncate">{item.name}</span>
                        </Link>
                      );
                    })
                  ) : (
                    <div className="rounded-xl px-2.5 py-2 text-sm text-gray-500">
                      Нет доступных приложений
                    </div>
                  )}
                </div>
              </section>

              {visibleSecondaryMenuItems.length > 0 && (
                <section className="space-y-1.5">
                  <div className="px-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                    Сервис
                  </div>
                  <div className="space-y-0.5">
                    {visibleSecondaryMenuItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = isRouteActive(location.pathname, item.route);

                      return (
                        <Link
                          key={item.route}
                          to={item.route}
                          className={getItemClassName(isActive)}
                          onClick={closeSidebar}
                        >
                          <span className={getIconWrapClassName(isActive)}>
                            <Icon className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 flex-1 truncate">{item.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                </section>
              )}

              {user?.hasAdminAccess && (
                <section className="space-y-1.5">
                  <div className="px-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                    Управление
                  </div>
                  <div className="space-y-0.5">
                    <Link
                      to="/admin"
                      className={getItemClassName(adminSectionActive)}
                      onClick={closeSidebar}
                    >
                      <span className={getIconWrapClassName(adminSectionActive)}>
                        <Shield className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1 truncate">Админ</span>
                    </Link>
                  </div>
                </section>
              )}
            </nav>
          </div>
        </div>

        <div className="flex-shrink-0 border-t border-gray-100 bg-white/90 p-4 backdrop-blur-sm">
          <div className="rounded-2xl bg-gray-50/60 p-3">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-gray-500">
                <User className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                  Аккаунт
                </p>
                <p className="truncate text-sm font-semibold text-gray-900">
                  {getUserDisplayName()}
                </p>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-red-50 px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-100"
            >
              <LogOut className="h-4 w-4" />
              Выйти
            </button>
          </div>
        </div>
      </aside>

      <button
        onClick={() => setIsSidebarOpen(true)}
        className="fixed left-4 top-4 z-50 rounded-xl border border-gray-200/70 bg-white/90 p-2 shadow-lg backdrop-blur-sm transition-colors hover:bg-white lg:hidden"
        style={isControlled ? { display: 'none' } : undefined}
        aria-hidden={isControlled ? true : undefined}
        tabIndex={isControlled ? -1 : undefined}
      >
        <svg className="h-5 w-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      <NotificationsModal
        isOpen={notificationsOpen}
        onClose={() => {
          setNotificationsOpen(false);
          loadNotificationStats();
          window.dispatchEvent(new Event('notificationUpdate'));
        }}
      />
    </>
  );
};

export default Sidebar;
